import { useEffect, useRef, useState } from 'react';
import type { FollowModeState } from '@annoteer/platform-adapter';
import type { DocumentInfo } from '../types/document.js';
import { useAuthFetch } from './useAuthFetch.js';
import { resolvePresenterView } from '../utils/presenter-view.js';
import { shouldBeginFetch, releaseFetch } from '../utils/follow-fetch.js';

const SERVER_URL = import.meta.env.VITE_SERVER_URL || '';

/**
 * Fetches and loads the presenter's document when following and the document ID changes.
 * Returns the loaded DocumentInfo so the parent can set it as the active document.
 */
export function useFollowDocument(
  followModeState: FollowModeState | null,
  isFollowing: boolean,
  currentDocumentId: string | undefined | null,
): {
  followDocument: DocumentInfo | null;
  isLoadingFollow: boolean;
} {
  const { authFetch } = useAuthFetch();
  const [followDocument, setFollowDocument] = useState<DocumentInfo | null>(null);
  const [isLoadingFollow, setIsLoadingFollow] = useState(false);
  const fetchingRef = useRef<string | null>(null);

  // Only the presenter's document. Live Share also echoes our OWN view state
  // back (isLocalValue), and chasing that would mean fetching whatever we
  // already have — or thrashing between two documents.
  const presenterDocId = resolvePresenterView(followModeState)?.documentId ?? null;

  useEffect(() => {
    // Act only when following, on a document we are not already showing or
    // already asking for. See follow-fetch.ts for why the latch has to be
    // released on cancellation as well as on completion.
    if (!shouldBeginFetch(fetchingRef.current, presenterDocId, currentDocumentId, isFollowing)) {
      return;
    }

    const wanted = presenterDocId as string;
    fetchingRef.current = wanted;

    let cancelled = false;

    async function fetchDocument() {
      setIsLoadingFollow(true);
      try {
        const response = await authFetch(`${SERVER_URL}/api/documents/${presenterDocId}`);
        if (!response.ok || cancelled) return;

        const result = (await response.json()) as {
          documentId: string;
          fileName: string;
          mimeType: string;
          documentType: 'pdf' | 'image' | 'presentation';
          base64: string;
        };

        if (cancelled) return;

        // Decode base64 to Uint8Array (same pattern as DocumentUpload)
        const binary = atob(result.base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
          bytes[i] = binary.charCodeAt(i);
        }

        setFollowDocument({
          documentId: result.documentId,
          fileName: result.fileName,
          mimeType: result.mimeType,
          documentType: result.documentType,
          data: bytes,
        });
      } catch (err) {
        console.error('Failed to load presenter document:', err);
      } finally {
        // A cancelled attempt settles AFTER its replacement has claimed the
        // latch, so it must not touch it — releaseFetch is only safe here
        // because this branch runs when we are still the current attempt.
        if (!cancelled) {
          setIsLoadingFollow(false);
          fetchingRef.current = releaseFetch(fetchingRef.current, wanted);
        }
      }
    }

    fetchDocument();

    return () => {
      cancelled = true;
      // Release the latch on the way out. Without this a cancelled fetch left
      // `wanted` latched forever, so the guard above refused every later
      // attempt at the same document and the follower never saw it — the bug
      // reported from a phone, where the fetch rarely beats the ~4s blink.
      setIsLoadingFollow(false);
      fetchingRef.current = releaseFetch(fetchingRef.current, wanted);
    };
  }, [isFollowing, presenterDocId, currentDocumentId, authFetch]);

  return { followDocument, isLoadingFollow };
}
